import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import ConfirmModal from '../components/ConfirmModal';
import EditModal from '../components/EditModal';
import styles from './TablePage.module.css';

export default function TablePage() {
  const { tableName } = useParams<{ tableName: string }>();
  const [rows, setRows] = useState<any[]>([]);
  const [columns, setColumns] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchText, setSearchText] = useState('');

  // Delete modal
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);

  // Edit/Create modal
  const [editTarget, setEditTarget] = useState<any | null>(null); // null = closed, {} = new row
  const [isCreating, setIsCreating] = useState(false);

  const fetchData = useCallback(async () => {
    if (!tableName) return;
    setLoading(true);
    setError('');
    const { data, error } = await supabase.from(tableName).select('*').limit(200);
    if (error) {
      setError(error.message);
    } else if (data && data.length > 0) {
      setColumns(Object.keys(data[0]));
      setRows(data);
    } else {
      setColumns([]);
      setRows([]);
    }
    setLoading(false);
  }, [tableName]);

  useEffect(() => {
    fetchData();
    setSearchText('');
  }, [fetchData]);

  // Primary key heuristic
  const getPK = (row: any) =>
    row.id ?? row.uuid ?? Object.values(row)[0];

  async function handleDelete(row: any) {
    const pk = row.id ?? row.uuid;
    if (!pk || !tableName) return;
    const pkField = row.id !== undefined ? 'id' : 'uuid';
    const { error } = await supabase.from(tableName).delete().eq(pkField, pk);
    if (error) alert('삭제 실패: ' + error.message);
    else {
      setRows(prev => prev.filter(r => getPK(r) !== pk));
      setDeleteTarget(null);
    }
  }

  async function handleSave(formData: Record<string, any>) {
    if (!tableName) return;
    if (isCreating) {
      const { data, error } = await supabase.from(tableName).insert([formData]).select();
      if (error) { alert('추가 실패: ' + error.message); return; }
      if (data) setRows(prev => [...prev, ...data]);
    } else {
      const pk = editTarget.id ?? editTarget.uuid;
      const pkField = editTarget.id !== undefined ? 'id' : 'uuid';
      const { data, error } = await supabase.from(tableName).update(formData).eq(pkField, pk).select();
      if (error) { alert('수정 실패: ' + error.message); return; }
      if (data) setRows(prev => prev.map(r => getPK(r) === pk ? data[0] : r));
    }
    setEditTarget(null);
    setIsCreating(false);
  }

  const filtered = rows.filter(row =>
    searchText === '' ||
    Object.values(row).some(v => String(v).toLowerCase().includes(searchText.toLowerCase()))
  );

  if (loading) return <div className={styles.loading}>불러오는 중...</div>;
  if (error) return (
    <div className={styles.errorBox}>
      <div className={styles.errorTitle}>테이블을 불러올 수 없어요</div>
      <div className={styles.errorMsg}>{error}</div>
      <div className={styles.errorHint}>
        Supabase에서 <strong>{tableName}</strong> 테이블이 아직 생성되지 않았거나 RLS 정책이 필요합니다.
      </div>
    </div>
  );

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>{tableName}</h1>
          <span className={styles.count}>{filtered.length}개 레코드</span>
        </div>
        <div className={styles.actions}>
          <input
            className={`input ${styles.search}`}
            placeholder="검색..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
          />
          <button className="btn btn-ghost" onClick={fetchData}>↺ 새로고침</button>
          <button
            className="btn btn-primary"
            onClick={() => { setIsCreating(true); setEditTarget({}); }}
          >
            + 추가
          </button>
        </div>
      </div>

      {/* Table */}
      {rows.length === 0 ? (
        <div className={styles.empty}>
          <div className={styles.emptyIcon}>📭</div>
          <div>데이터가 없습니다</div>
        </div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className="data-table">
            <thead>
              <tr>
                {columns.map(col => <th key={col}>{col}</th>)}
                <th style={{ width: 120 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row, i) => (
                <tr key={i}>
                  {columns.map(col => (
                    <td key={col} title={String(row[col] ?? '')}>
                      {row[col] === null ? (
                        <span style={{ color: 'rgba(255,255,255,0.25)', fontStyle: 'italic' }}>null</span>
                      ) : String(row[col])}
                    </td>
                  ))}
                  <td>
                    <div className={styles.rowActions}>
                      <button className="btn btn-ghost" style={{ padding: '4px 10px', fontSize: 12 }}
                        onClick={() => { setIsCreating(false); setEditTarget(row); }}>
                        편집
                      </button>
                      <button className="btn btn-danger" style={{ padding: '4px 10px', fontSize: 12 }}
                        onClick={() => setDeleteTarget(row)}>
                        삭제
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete confirm modal */}
      {deleteTarget && (
        <ConfirmModal
          title="정말 삭제할까요?"
          message="이 작업은 되돌릴 수 없습니다."
          onConfirm={() => handleDelete(deleteTarget)}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      {/* Edit / Create modal */}
      {editTarget !== null && (
        <EditModal
          columns={isCreating ? columns : Object.keys(editTarget)}
          initialData={isCreating ? {} : editTarget}
          isCreating={isCreating}
          onSave={handleSave}
          onClose={() => { setEditTarget(null); setIsCreating(false); }}
        />
      )}
    </div>
  );
}
